/**
 * NodeEditorModal - Modal wrapper for Company Info mode node editing
 *
 * Opens when user clicks a node in Company Info filter mode.
 * Routes to appropriate editor based on node type:
 * - company_id -> CompanyInfoEditor (structured form)
 * - human_capital_id -> EmployeeTableEditor
 * - competitor_id -> CompetitorTableEditor
 * - Other nodes -> TextNoteEditor (simple text area)
 */

import { useState, useEffect, useCallback } from 'react'
import { companyProfileApi, NodeValueResponse } from '@services/companyProfileApi'
import { TextNoteEditor } from './TextNoteEditor'
import { CompanyInfoEditor } from './CompanyInfoEditor'
import { EmployeeTableEditor } from './EmployeeTableEditor'
import { CompetitorTableEditor } from './CompetitorTableEditor'
import './NodeEditorModal.css'

export interface NodeEditorModalProps {
  isOpen: boolean
  nodeId: string
  nodeLabel: string
  nodeDescription?: string
  companyId: number
  onClose: () => void
  onSave: (nodeId: string, hasValue: boolean) => void
}

export function NodeEditorModal({
  isOpen,
  nodeId,
  nodeLabel,
  nodeDescription,
  companyId,
  onClose,
  onSave
}: NodeEditorModalProps) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nodeData, setNodeData] = useState<NodeValueResponse | null>(null)

  // Load node value when modal opens
  useEffect(() => {
    if (isOpen && nodeId && companyId) {
      loadNodeValue()
    }
  }, [isOpen, nodeId, companyId])

  const loadNodeValue = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await companyProfileApi.getNodeValue(companyId, nodeId)
      setNodeData(data)
    } catch (err: any) {
      console.error('[NodeEditorModal] Error loading node value:', err)
      // 404 is expected for nodes without values
      if (err.response?.status !== 404) {
        setError(err.message || 'Failed to load node value')
      }
      setNodeData(null)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = useCallback(async (value: string) => {
    setSaving(true)
    setError(null)
    try {
      await companyProfileApi.saveNodeValue(companyId, nodeId, value)
      const hasValue = value.trim().length > 0
      onSave(nodeId, hasValue)
      onClose()
    } catch (err: any) {
      console.error('[NodeEditorModal] Error saving node value:', err)
      setError(err.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }, [companyId, nodeId, onSave, onClose])

  const handleCancel = useCallback(() => {
    setError(null)
    onClose()
  }, [onClose])

  if (!isOpen) return null

  // Determine which editor to render based on node type
  const renderEditor = () => {
    if (loading) {
      return (
        <div className="node-editor-loading">
          <div className="loading-spinner" />
          <p>Loading...</p>
        </div>
      )
    }

    const currentValue = nodeData?.node_value || ''

    switch (nodeId) {
      case 'company_id':
        return (
          <CompanyInfoEditor
            value={currentValue}
            onSave={handleSave}
            onCancel={handleCancel}
            saving={saving}
          />
        )

      case 'human_capital_id':
        return (
          <EmployeeTableEditor
            value={currentValue}
            onSave={handleSave}
            onCancel={handleCancel}
            saving={saving}
          />
        )

      case 'competitor_id':
        return (
          <CompetitorTableEditor
            value={currentValue}
            onSave={handleSave}
            onCancel={handleCancel}
            saving={saving}
          />
        )

      default:
        return (
          <TextNoteEditor
            value={currentValue}
            onSave={handleSave}
            onCancel={handleCancel}
            saving={saving}
          />
        )
    }
  }

  return (
    <div className="node-editor-overlay" onClick={handleCancel}>
      <div className="node-editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="node-editor-header">
          <div className="node-editor-title">
            <h3>{nodeLabel}</h3>
            <span className="node-id">[{nodeId}]</span>
          </div>
          <button className="close-btn" onClick={handleCancel}>
            &times;
          </button>
        </div>

        {nodeDescription && (
          <div className="node-description">
            {nodeDescription}
          </div>
        )}

        {error && (
          <div className="node-editor-error">
            {error}
          </div>
        )}

        <div className="node-editor-content">
          {renderEditor()}
        </div>
      </div>
    </div>
  )
}

export default NodeEditorModal
